import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'preact/hooks';

import { getProductsQueryOptions } from '@/queries/products';
import { useSelector } from '@/redux/helper';

/**
 * The product the visitor picked, and the habitat the flow is about.
 *
 * The machine records a choice as ids — that is all it needs to replay a checkout, and all that is
 * safe to put on a URL (ADR 0006). Anything that has to *show* the choice needs the entities behind
 * those ids, which is what this resolves.
 *
 * Both are optional by nature: the Sign-In Flow has no product, and a flow opened away from a
 * habitat has no habitat.
 */
const useChosenProduct = () => {
  const productChoice = useSelector((state) => state.modals.speedyFlow.machine?.productChoice);
  const habitatId = useSelector((state) => state.modals.speedyFlow.habitatId);
  const productId = productChoice?.productId;

  /**
   * Asked *for a habitat*, because eligibility is.
   *
   * A habitat-scoped product judged with no habitat in hand comes back refused for "No habitat
   * selected" — a false refusal that would have blocked a perfectly good purchase. The habitat
   * asked about is the one the checkout will actually name, so the answer matches the request that
   * follows it.
   *
   * It does not narrow the list: the API uses `habitatId` only to scope eligibility, so any product
   * is still findable by id.
   */
  const eligibilityHabitatId = productChoice?.associatedHabitatIds?.[0] ?? habitatId;

  /*
   * The same id resolves the habitat, so a card that names a species names the one that was bought.
   * They are the same habitat in the ordinary case; they part company when a choice was resumed
   * from a link, where the selection carries the habitat and the flow was not opened in one.
   */
  const habitat = useSelector((state) => state.allHabitats.find((h) => h._id === eligibilityHabitatId));

  const {
    data: product,
    isSuccess: listed,
    refetch,
  } = useQuery({
    ...getProductsQueryOptions(eligibilityHabitatId ? { habitatId: eligibilityHabitatId } : undefined),
    select: (data) => data.list.find((p) => p._id === productId),
    enabled: !!productId,
  });

  /**
   * Ask the list again, and wait for the answer.
   *
   * Handed out rather than left to the caller to arrange, because the question is scoped — the key
   * carries `eligibilityHabitatId` — and a caller that built its own fetch could easily ask a
   * different one than the check above judges. One place decides what "the list" means here.
   *
   * `cancelRefetch: false` so this joins a refetch already in flight instead of cancelling it and
   * starting a third. The one it joins was issued after the session changed, which is exactly the
   * answer worth waiting for.
   */
  const reconcile = useCallback(() => refetch({ cancelRefetch: false }), [refetch]);

  /**
   * Whether the list has answered, which is a different question from whether it named the product.
   *
   * `product` is `undefined` in two situations that mean opposite things: the request is still in
   * flight, or it came back and this product was not on it — which is how a product priced in no
   * currency this visitor can be billed in disappears. Only the callers that have to tell those
   * apart use this; see `isChoiceStale`.
   */
  return { product, habitat, listed, reconcile };
};

export default useChosenProduct;
